// The page's one writer of the Screen Wake Lock (core/screenAwake.ts, PARITY P304). The call, the
// voice recorder and the remote-support viewer each hold a KEY; the lock is requested while any key
// is held and released when the last one goes. The browser drops the lock whenever the tab is hidden,
// so it is asked for again when the tab comes back while something still holds it.
//
// Shared by the web app and the Windows call host bundle (callManagerCore imports it): there it is
// the same `navigator.wakeLock` of the WebView2.
import { ScreenAwake } from '../core/screenAwake';

interface Sentinel {
  release(): Promise<void>;
  addEventListener?(type: 'release', fn: () => void): void;
}

type WakeLockApi = { request(type: 'screen'): Promise<Sentinel> };

let sentinel: Sentinel | null = null;
let requesting = false;
let wired = false;

function api(): WakeLockApi | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { wakeLock?: WakeLockApi }).wakeLock ?? null;
}

function acquire(): void {
  const lock = api();
  if (!lock || sentinel || requesting) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  requesting = true;
  lock
    .request('screen')
    .then((s) => {
      requesting = false;
      if (!screenAwake.isHeld) {
        void s.release().catch(() => undefined);
        return;
      }
      sentinel = s;
      s.addEventListener?.('release', () => {
        if (sentinel === s) sentinel = null;
      });
    })
    .catch(() => {
      requesting = false; // not granted (no user activation yet, battery saver): the hold still counts
    });
}

function drop(): void {
  const s = sentinel;
  sentinel = null;
  void s?.release().catch(() => undefined);
}

function wire(): void {
  if (wired || typeof document === 'undefined') return;
  wired = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && screenAwake.isHeld) {
      sentinel = null; // the browser released it while hidden
      acquire();
    }
  });
}

export const screenAwake = new ScreenAwake((held) => {
  wire();
  if (held) acquire();
  else drop();
});

/** For the diagnostics and the tests: is the lock in hand right now? */
export function screenAwakeLockHeld(): boolean {
  return sentinel !== null;
}
